"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { MeshGradient, PulsingBorder } from "@paper-design/shaders-react"
import { motion } from "framer-motion"

const WHATSAPP_URL = "https://wa.me/+17874222734"
const TACO_HERO_IMAGE_URL = "https://images.unsplash.com/photo-1565299585323-38d6b0865b47?auto=format&fit=crop&w=2200&q=85"

export default function ShaderShowcase() {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isActive, setIsActive] = useState(false)
  const sparks = useMemo(
    () =>
      Array.from({ length: 6 }, (_, i) => ({
        id: i,
        left: `${22 + i * 9}%`,
        top: `${28 + ((i * 13) % 38)}%`,
        x: i % 2 === 0 ? 8 : -8,
      })),
    [],
  )

  useEffect(() => {
    const handleMouseEnter = () => setIsActive(true)
    const handleMouseLeave = () => setIsActive(false)
    const container = containerRef.current

    if (container) {
      container.addEventListener("mouseenter", handleMouseEnter)
      container.addEventListener("mouseleave", handleMouseLeave)
    }

    return () => {
      if (container) {
        container.removeEventListener("mouseenter", handleMouseEnter)
        container.removeEventListener("mouseleave", handleMouseLeave)
      }
    }
  }, [])

  return (
    <div ref={containerRef} className="min-h-screen bg-black relative overflow-hidden">
      <svg className="absolute inset-0 w-0 h-0">
        <defs>
          <filter id="glass-effect" x="-50%" y="-50%" width="200%" height="200%">
            <feTurbulence baseFrequency="0.005" numOctaves="1" result="noise" />
            <feDisplacementMap in="SourceGraphic" in2="noise" scale="0.3" />
            <feColorMatrix
              type="matrix"
              values="1 0 0 0 0.02
                      0 1 0 0 0.02
                      0 0 1 0 0.05
                      0 0 0 0.9 0"
              result="tint"
            />
          </filter>
          <filter id="gooey-filter" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur" />
            <feColorMatrix
              in="blur"
              mode="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 19 -9"
              result="gooey"
            />
            <feComposite in="SourceGraphic" in2="gooey" operator="atop" />
          </filter>
          <filter id="logo-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id="hero-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FFF8EA" />
            <stop offset="34%" stopColor="#4EBFBF" />
            <stop offset="68%" stopColor="#7D468C" />
            <stop offset="100%" stopColor="#F2785C" />
          </linearGradient>
          <filter id="text-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
      </svg>

      <MeshGradient
        className="absolute inset-0 z-0 w-full h-full opacity-70"
        colors={["#0F1C2E", "#233559", "#7D468C", "#4EBFBF", "#F2785C", "#6E1F1A"]}
        speed={isActive ? 0.42 : 0.26}
        distortion={0.72}
        swirl={0.36}
        grainOverlay={0.08}
      />
      <motion.div
        className="absolute inset-0 z-[1] bg-cover bg-center md:bg-[position:68%_50%]"
        style={{ backgroundImage: `url(${TACO_HERO_IMAGE_URL})` }}
        initial={{ scale: 1.06, opacity: 0 }}
        animate={{ scale: isActive ? 1.1 : 1.06, opacity: 1 }}
        transition={{ duration: 1.1, ease: "easeOut" }}
        aria-hidden="true"
      />
      <MeshGradient
        className="absolute inset-0 z-[3] w-full h-full opacity-30 mix-blend-screen"
        colors={["#0F1C2E", "#FFF8EA", "#7D468C", "#4EBFBF", "#F7E7C6"]}
        speed={isActive ? 0.28 : 0.18}
        distortion={0.44}
        swirl={0.72}
        grainMixer={0.18}
      />
      <div className="absolute inset-0 z-[4] bg-[radial-gradient(circle_at_72%_46%,rgba(247,231,198,0.2),transparent_32%),radial-gradient(circle_at_86%_80%,rgba(47,125,78,0.2),transparent_28%),linear-gradient(90deg,rgba(15,28,46,0.96)_0%,rgba(15,28,46,0.78)_42%,rgba(110,31,26,0.3)_100%)]" />
      <div className="absolute inset-x-0 bottom-0 z-[5] h-40 bg-gradient-to-t from-[#0F1C2E] to-transparent" />

      <header className="relative z-20 flex items-center justify-between p-6 md:p-10">
        <motion.a
          href="/"
          className="relative flex items-center group cursor-pointer"
          whileHover={{ scale: 1.04 }}
          transition={{ type: "spring", stiffness: 400, damping: 12 }}
          aria-label="Mikel Media"
        >
          <img
            src="/logo-light.svg"
            alt="Mikel Media"
            className="h-9 w-auto opacity-90 transition-all duration-300 group-hover:opacity-100"
            style={{ filter: "url(#logo-glow)" }}
          />
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
            {sparks.map((spark) => (
              <motion.div
                key={spark.id}
                className="absolute w-1 h-1 bg-white/60 rounded-full"
                style={{ left: spark.left, top: spark.top }}
                animate={{
                  y: [-10, -22, -10],
                  x: [0, spark.x, 0],
                  opacity: [0, 1, 0],
                  scale: [0, 1, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Number.POSITIVE_INFINITY,
                  delay: spark.id * 0.2,
                  ease: "easeInOut",
                }}
              />
            ))}
          </div>
        </motion.a>

        <nav className="hidden md:flex items-center space-x-2">
          <a href="#diagnostico" className="text-white/80 hover:text-white text-xs font-light px-3 py-2 rounded-full hover:bg-white/10 transition-all duration-200">
            Diagnóstico
          </a>
          <a href="#paquetes" className="text-white/80 hover:text-white text-xs font-light px-3 py-2 rounded-full hover:bg-white/10 transition-all duration-200">
            Paquetes
          </a>
          <a href="#plan" className="text-white/80 hover:text-white text-xs font-light px-3 py-2 rounded-full hover:bg-white/10 transition-all duration-200">
            Plan
          </a>
        </nav>

        <div id="gooey-btn" className="relative flex items-center group" style={{ filter: "url(#gooey-filter)" }}>
          <a
            href="#paquetes"
            className="absolute right-0 px-2.5 py-2 rounded-full bg-white text-black font-normal text-xs transition-all duration-300 hover:bg-white/90 cursor-pointer h-8 flex items-center justify-center -translate-x-10 group-hover:-translate-x-20 z-0"
            aria-label="Ver paquetes"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M17 7H7M17 7V17" />
            </svg>
          </a>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            className="px-5 md:px-6 py-2 rounded-full bg-white text-black font-normal text-xs transition-all duration-300 hover:bg-white/90 cursor-pointer h-8 flex items-center z-10"
          >
            WhatsApp
          </a>
        </div>
      </header>

      <main className="absolute bottom-8 left-6 right-6 md:left-10 md:right-auto z-20 max-w-3xl">
        <div className="text-left">
          <motion.div
            className="inline-flex items-center px-4 py-2 rounded-full bg-white/5 backdrop-blur-sm mb-6 relative border border-white/10"
            style={{ filter: "url(#glass-effect)" }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="absolute top-0 left-1 right-1 h-px bg-gradient-to-r from-transparent via-[#4EBFBF]/50 to-transparent rounded-full" />
            <span className="text-white/90 text-sm font-medium relative z-10 tracking-wide">
              Propuesta Digital · Mayo 2026
            </span>
          </motion.div>

          <motion.h1
            className="text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-6 leading-none tracking-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <motion.span
              className="block font-light text-white/90 text-4xl md:text-5xl lg:text-6xl mb-2 tracking-wider"
              style={{
                background: "linear-gradient(135deg, #FFF8EA 0%, #4EBFBF 28%, #7D468C 62%, #F2785C 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                filter: "url(#text-glow)",
              }}
              animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
              transition={{ duration: 8, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
            >
              La Gacha
            </motion.span>
            <span className="block font-black text-white drop-shadow-2xl">Taqueria</span>
            <span className="block font-light text-white/80 italic">Mexicana</span>
          </motion.h1>

          <motion.p
            className="text-base md:text-lg font-light text-white/72 mb-8 leading-relaxed max-w-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            El pastor ya tiene historia, textura y público. Ahora necesita una presencia digital que venda esa
            experiencia todos los días.
          </motion.p>

          <motion.div
            className="flex items-center gap-4 md:gap-6 flex-wrap"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
          >
            <motion.a
              href="#paquetes"
              className="px-8 md:px-10 py-4 rounded-full bg-transparent border-2 border-white/30 text-white font-medium text-sm transition-all duration-300 hover:bg-white/10 hover:border-[#4EBFBF]/70 hover:text-white cursor-pointer backdrop-blur-sm"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Ver propuesta
            </motion.a>
            <motion.a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              className="px-8 md:px-10 py-4 rounded-full bg-gradient-to-r from-[#7D468C] via-[#F2785C] to-[#2F7D4E] text-white font-semibold text-sm transition-all duration-300 hover:from-[#233559] hover:to-[#F2785C] cursor-pointer shadow-lg hover:shadow-xl"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Hablemos
            </motion.a>
          </motion.div>
        </div>
      </main>

      <div className="hidden sm:block absolute bottom-8 right-8 z-30">
        <div className="relative w-20 h-20 flex items-center justify-center">
          <PulsingBorder
            colors={["#7D468C", "#4EBFBF", "#F2785C", "#2F7D4E", "#F7E7C6"]}
            colorBack="#00000000"
            speed={1.5}
            roundness={1}
            thickness={0.1}
            softness={0.2}
            intensity={5}
            spots={4}
            spotSize={0.1}
            pulse={0.1}
            smoke={0.5}
            smokeSize={4}
            scale={0.65}
            rotation={0}
            frame={9161408.251009725}
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "50%",
            }}
          />

          <motion.svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 100 100"
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
            style={{ transform: "scale(1.6)" }}
          >
            <defs>
              <path id="circle" d="M 50, 50 m -38, 0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
            </defs>
            <text className="text-sm fill-white/80 font-medium">
              <textPath href="#circle" startOffset="0%">
                Mikel Media · La Gacha · San Juan · Digital Growth ·
              </textPath>
            </text>
          </motion.svg>
        </div>
      </div>
    </div>
  )
}
